/**
 * Turn folding — pure logic, no React.
 *
 * One assistant message is one turn. Once it settles, everything the model did
 * on the way to its answer (reasoning, tool calls, intermediate commentary)
 * collapses behind a single "Worked for 42s" row, and only the answer plus any
 * user-facing results (images, renders, tables, HTML artifacts) stay visible.
 *
 * The semantics are ported from t3code's `deriveTurnFolds`
 * (apps/web/src/components/chat/MessagesTimeline.logic.ts), translated from
 * T3's timeline entries to assistant-ui message parts:
 *
 *   T3 "terminal assistant message"   → the last non-empty text part
 *   T3 "work entry"                    → a tool-call / reasoning / earlier text part
 *   T3 "persistent resource card"      → a deliverable tool part (see DELIVERABLE_TOOLS)
 *   T3 "inProgress / failed trailing"  → an unfinished / errored tool after the answer
 *   T3 "lone compaction row"           → a fold that would hide only empty parts
 */
import type {
  ReasoningMessagePart,
  TextMessagePart,
  ThreadAssistantMessagePart,
  ToolCallMessagePart,
  ToolCallMessagePartStatus,
} from '@assistant-ui/react';

// ── Part shape ──────────────────────────────────────────────────────────────

/**
 * The slice of a tool-call part this module reads. Real `ToolCallMessagePart`s
 * satisfy it; `status` is optional because raw `message.content` parts don't
 * carry one (assistant-ui computes it per render).
 */
export type FoldToolPart = Pick<ToolCallMessagePart, 'type' | 'toolName'> & {
  readonly toolCallId?: string;
  readonly args?: unknown;
  readonly result?: unknown;
  readonly isError?: boolean;
  readonly status?: ToolCallMessagePartStatus;
};

/** Anything `ThreadAssistantMessagePart` is assignable to. */
export type FoldPart =
  | TextMessagePart
  | ReasoningMessagePart
  | FoldToolPart
  | Exclude<ThreadAssistantMessagePart, TextMessagePart | ReasoningMessagePart | ToolCallMessagePart>;

function isToolPart(part: FoldPart): part is FoldToolPart {
  return part.type === 'tool-call';
}

function hasText(part: FoldPart): boolean {
  return (part.type === 'text' || part.type === 'reasoning') && part.text.trim().length > 0;
}

/** Text, reasoning and tool calls are process; images, files, sources and data are output and never fold. */
function isFoldableKind(part: FoldPart): boolean {
  return part.type === 'text' || part.type === 'reasoning' || part.type === 'tool-call';
}

/** A tool whose call never finished: still running, awaiting a human, or cut off (settled with no result). */
function isUnfinishedTool(part: FoldToolPart): boolean {
  if (part.isError) return false;
  const status = part.status?.type;
  if (status === 'running' || status === 'requires-action') return true;
  if (status === 'incomplete') return true;
  if (status === 'complete') return false;
  return part.result === undefined;
}

// ── Adapted from t3code ─────────────────────────────────────────────────────
// Adapted from t3code (https://github.com/pingdotgg/t3code), © 2026 T3 Tools Inc., MIT License

/** Adapted from t3code (https://github.com/pingdotgg/t3code), © 2026 T3 Tools Inc., MIT License */
export function formatDuration(durationMs: number): string {
  if (!Number.isFinite(durationMs) || durationMs < 0) return '0ms';
  if (durationMs < 1_000) return `${Math.max(1, Math.round(durationMs))}ms`;
  if (durationMs < 10_000) {
    const tenths = Math.round(durationMs / 100) / 10;
    return tenths >= 10 ? '10s' : `${tenths.toFixed(1)}s`;
  }
  if (durationMs < 60_000) return `${Math.round(durationMs / 1_000)}s`;
  const totalSeconds = Math.round(durationMs / 1_000);
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);
  return parts.join(' ');
}

/** Adapted from t3code (https://github.com/pingdotgg/t3code), © 2026 T3 Tools Inc., MIT License */
export function computeElapsedMs(startIso: string, endIso: string): number | null {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.max(0, end - start);
}

// ── Deliverables ────────────────────────────────────────────────────────────

/**
 * Tools whose card *is* the result the user asked for, so it stays visible
 * outside the fold. Taken from the `makeAssistantToolUI` registrations in
 * ./tool-renderers (ScriptToolUI and TodosToolUI are process, not output).
 */
export const DELIVERABLE_TOOLS: ReadonlySet<string> = new Set([
  'generate_image', // ImageGenToolUI
  'vc_render_status', // RenderJobToolUI — video render
  'vc_get_preview', // RenderJobToolUI — scene preview
  'vfx_get_job', // RenderJobToolUI — pipeline job
  'batch_screenshot', // BatchScreenshotToolUI
  'batch_render_video', // BatchRenderVideoToolUI
  'run_custom_query', // DataTableToolUI
  'ask_user', // AskUserToolUI — the questions, and later the answers
]);

const isHtmlPath = (p: unknown): p is string => typeof p === 'string' && /\.html?$/i.test(p);

/** Mirrors `extractHtmlTargets` in HtmlArtifactCard.tsx (not imported: it drags in React and the API client). */
function writesHtml(toolName: string, args: unknown): boolean {
  if (typeof args !== 'object' || args === null) return false;
  const a = args as Record<string, unknown>;
  if (toolName === 'fs_write') return isHtmlPath(a.target_file);
  if (toolName === 'fs_batch_write') {
    const files = a.files;
    return typeof files === 'object' && files !== null && Object.keys(files).some(isHtmlPath);
  }
  return false;
}

/**
 * Whether a part renders as a user-facing result.
 *
 * - Named deliverable tools count unless they failed — a failed image job
 *   before a successful retry is process, and folds like any failed step.
 * - An `.html` write counts only when the fallback would actually render
 *   HtmlArtifactCard: not failed and not interrupted (it has a result, or is
 *   still running).
 */
export function isDeliverablePart(part: FoldPart): boolean {
  if (!isToolPart(part) || part.isError) return false;
  if (DELIVERABLE_TOOLS.has(part.toolName)) return true;
  if (writesHtml(part.toolName, part.args)) {
    return part.result !== undefined || part.status?.type === 'running';
  }
  return false;
}

// ── Classification & summary ────────────────────────────────────────────────

export type ToolCategory = 'read' | 'search' | 'edit' | 'command' | 'other';

const EXACT_CATEGORY: Record<string, ToolCategory> = {
  fs_read: 'read',
  fs_ls: 'read',
  fs_fetch: 'read',
  fetch_url: 'read',
  fs_grep: 'search',
  web_search: 'search',
  fs_write: 'edit',
  fs_edit: 'edit',
  fs_batch_write: 'edit',
  fs_delete: 'edit',
  fs_move: 'edit',
  apply_patch: 'edit',
  execute_command: 'command',
  todo_write: 'other',
};

/**
 * Buckets a tool by name. Our own tools are listed exactly; anything else
 * falls through name heuristics so provider-native tools (read_file, glob,
 * shell…) still land somewhere sensible. API reads/mutations (ads, BigQuery)
 * are "other" — calling an ad mutation an "edit" would read as a file edit.
 */
export function classifyTool(toolName: string, args?: unknown): ToolCategory {
  const exact = EXACT_CATEGORY[toolName];
  if (exact) return exact;
  if (toolName === 'read_source') {
    const search = (args as Record<string, unknown> | undefined)?.search;
    return typeof search === 'string' && search ? 'search' : 'read';
  }
  const n = toolName.toLowerCase();
  if (/(^|_)(ads|api|query|bigquery|pivot)(_|$)/.test(n)) return 'other';
  if (/(shell|bash|exec|command|terminal|run_script)/.test(n)) return 'command';
  if (/(grep|glob|search|find)/.test(n)) return 'search';
  if (/(write|edit|patch|replace|create_file|delete|move|rename|str_replace)/.test(n)) return 'edit';
  if (/(read|^ls$|(^|_)list|view|(^|_)cat(_|$)|open_file|fetch)/.test(n)) return 'read';
  return 'other';
}

export interface FoldSummary {
  reads: number;
  edits: number;
  commands: number;
  searches: number;
  other: number;
  /** Non-empty reasoning blocks. */
  thoughts: number;
}

/** Counts tool calls by category and reasoning blocks over `indices` (default: every part). */
export function summarizeParts(parts: ReadonlyArray<FoldPart>, indices?: ReadonlyArray<number>): FoldSummary {
  const summary: FoldSummary = { reads: 0, edits: 0, commands: 0, searches: 0, other: 0, thoughts: 0 };
  const list = indices ?? parts.map((_, i) => i);
  for (const i of list) {
    const part = parts[i];
    if (!part) continue;
    if (part.type === 'reasoning') {
      if (hasText(part)) summary.thoughts += 1;
      continue;
    }
    if (!isToolPart(part)) continue;
    switch (classifyTool(part.toolName, part.args)) {
      case 'read': summary.reads += 1; break;
      case 'search': summary.searches += 1; break;
      case 'edit': summary.edits += 1; break;
      case 'command': summary.commands += 1; break;
      default: summary.other += 1;
    }
  }
  return summary;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * "4 reads · 3 edits · 2 commands". Zero counts are dropped. Thoughts are
 * mentioned only when there were no tool calls at all — next to tools they
 * are noise. Empty string when there is nothing to say.
 */
export function formatSummary(summary: FoldSummary): string {
  const bits: string[] = [];
  if (summary.reads) bits.push(plural(summary.reads, 'read', 'reads'));
  if (summary.searches) bits.push(plural(summary.searches, 'search', 'searches'));
  if (summary.edits) bits.push(plural(summary.edits, 'edit', 'edits'));
  if (summary.commands) bits.push(plural(summary.commands, 'command', 'commands'));
  if (summary.other) bits.push(plural(summary.other, 'other tool', 'other tools'));
  if (bits.length === 0 && summary.thoughts) bits.push(plural(summary.thoughts, 'thought', 'thoughts'));
  return bits.join(' · ');
}

// ── Fold ────────────────────────────────────────────────────────────────────

export interface FoldMeta {
  /** The message is still streaming. */
  running: boolean;
  /** The message ended in an error (status incomplete/error). */
  hasError: boolean;
  /** The user stopped the response (status incomplete/cancelled). */
  aborted?: boolean;
  /** ISO timestamp the turn started (ideally the user's send time). */
  startedAt?: string;
  /** ISO timestamp the turn settled. */
  finishedAt?: string;
}

export interface MessageFold {
  /** Part indices that render inside the fold, ascending. */
  hiddenIndices: number[];
  /** Where the fold row renders: the first hidden part's index. */
  anchorIndex: number;
  /** "Worked for 42s" / "Worked" / "You stopped after 12s" / "You stopped this response". */
  label: string;
  durationMs: number | null;
  /** Hidden tool calls + hidden non-empty reasoning blocks. */
  stepCount: number;
  summary: FoldSummary;
  /** Open the fold by default. True for stopped turns, so the user sees where it stopped. */
  autoExpand: boolean;
}

/**
 * Decides which parts of a settled assistant message fold behind one row.
 *
 * Rules (T3 semantics, adapted):
 * 1. No fold while the message is running (T3: streaming groups never fold).
 * 2. No fold when the message ended in an error — the whole turn stays open so
 *    the failure reads in context (T3: runless turns with a failed item).
 *    An individual failed *tool* does not block the fold: before the answer it
 *    folds (the model recovered), after the answer it stays visible.
 * 3. A stopped (aborted) turn still folds, with a "You stopped…" label and
 *    `autoExpand: true`.
 * 4. The terminal part is the last non-empty text part; it stays visible.
 *    Everything foldable before it folds — reasoning, tools, earlier text.
 *    With no text at all, the last part is the terminal one and stays visible.
 * 5. Parts after the terminal part fold only when they are finished,
 *    successful tool calls (or empty/reasoning filler); unfinished or failed
 *    trailing tools stay visible.
 * 6. Deliverables (see `isDeliverablePart`) and non-process parts (image,
 *    file, source, data) never fold.
 * 7. No fold when the fold would hide nothing meaningful (only empty parts).
 */
export function deriveMessageFold(parts: ReadonlyArray<FoldPart>, meta: FoldMeta): MessageFold | null {
  if (meta.running || meta.hasError || parts.length === 0) return null;

  let terminalIndex = -1;
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i]!;
    if (p.type === 'text' && hasText(p)) { terminalIndex = i; break; }
  }
  if (terminalIndex === -1) terminalIndex = parts.length - 1;

  const hiddenIndices: number[] = [];
  parts.forEach((part, index) => {
    if (index === terminalIndex) return;
    if (!isFoldableKind(part) || isDeliverablePart(part)) return;
    if (index > terminalIndex && isToolPart(part) && (part.isError || isUnfinishedTool(part))) return;
    hiddenIndices.push(index);
  });

  const meaningful = hiddenIndices.some((i) => {
    const p = parts[i]!;
    return isToolPart(p) || hasText(p);
  });
  if (!meaningful) return null;

  const summary = summarizeParts(parts, hiddenIndices);
  const toolCount = summary.reads + summary.edits + summary.commands + summary.searches + summary.other;
  const durationMs = meta.startedAt && meta.finishedAt ? computeElapsedMs(meta.startedAt, meta.finishedAt) : null;
  const duration = durationMs !== null ? formatDuration(durationMs) : null;
  const label = meta.aborted
    ? duration ? `You stopped after ${duration}` : 'You stopped this response'
    : duration ? `Worked for ${duration}` : 'Worked';

  return {
    hiddenIndices,
    anchorIndex: hiddenIndices[0]!,
    label,
    durationMs,
    stepCount: toolCount + summary.thoughts,
    summary,
    autoExpand: Boolean(meta.aborted),
  };
}

// ── Live exploration grouping ───────────────────────────────────────────────

export interface ExplorationGroup {
  /** First part index of the run (a read/search tool). */
  start: number;
  /** Last part index of the run, inclusive (a read/search tool). */
  end: number;
  /** Part indices inside the run, including swallowed empty filler parts. */
  indices: number[];
  /** "Explored 3 files · 1 search" / "Exploring 2 files" while one is in flight. */
  label: string;
}

/**
 * For the live view: collapses runs of ≥2 consecutive read/search tool calls
 * into one row. `indices` limits and orders the parts considered (e.g. the
 * visible ones); default is every part. Empty text/reasoning parts between
 * reads don't break a run; anything else (a non-empty thought, an edit, a
 * deliverable) does.
 */
export function groupExploration(parts: ReadonlyArray<FoldPart>, indices?: ReadonlyArray<number>): ExplorationGroup[] {
  const list = indices ?? parts.map((_, i) => i);
  const groups: ExplorationGroup[] = [];
  let run: number[] = [];
  let filler: number[] = [];

  const flush = () => {
    const tools = run.filter((i) => isToolPart(parts[i]!));
    if (tools.length >= 2) {
      let reads = 0;
      let searches = 0;
      let live = false;
      for (const i of tools) {
        const p = parts[i] as FoldToolPart;
        if (classifyTool(p.toolName, p.args) === 'search') searches += 1; else reads += 1;
        if (!p.isError && (p.status?.type === 'running' || (p.status === undefined && p.result === undefined))) live = true;
      }
      const bits: string[] = [];
      if (reads) bits.push(plural(reads, 'file', 'files'));
      if (searches) bits.push(plural(searches, 'search', 'searches'));
      groups.push({
        start: tools[0]!,
        end: tools[tools.length - 1]!,
        indices: run.slice(0, run.lastIndexOf(tools[tools.length - 1]!) + 1),
        label: `${live ? 'Exploring' : 'Explored'} ${bits.join(' · ')}`,
      });
    }
    run = [];
    filler = [];
  };

  for (const i of list) {
    const part = parts[i];
    if (!part) continue;
    if (isToolPart(part) && !isDeliverablePart(part)) {
      const cat = classifyTool(part.toolName, part.args);
      if (cat === 'read' || cat === 'search') {
        if (run.length) run.push(...filler);
        filler = [];
        run.push(i);
        continue;
      }
    }
    if (run.length && isFoldableKind(part) && part.type !== 'tool-call' && !hasText(part)) {
      filler.push(i);
      continue;
    }
    flush();
  }
  flush();
  return groups;
}
